import React, { useMemo, useState } from 'react'
import { Alert, Box, Button, Card, Chip, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { useSnackbar } from 'notistack'
import palette from '../../../theme/palette'
import PageTitle from '../../components/PageTitle'
import { tableHeadSx } from '../../common/sharedStyles'
import { useNabdMastersQuery, useNabdSetMemberActiveMutation } from '../../redux/ptlNabdApiSlice'
import { OWNERS, SUB_TEAM_LABEL, TEAM_SHORT_LABEL, canAct, rolesLabel } from './constants'
import KamMappingEditor from './components/KamMappingEditor'
import MemberDialog from './modals/MemberDialog'
import ConfirmDialog from './modals/ConfirmDialog'

/**
 * PTL → Non-ABD Shipments → PTL Team Master (artifact page "PTL Team Master"). Who sits in the PTL team and what each
 * person can be assigned: KAMs take client attributable delays, PTL Operations (Core Ops / FTR) take Prozo and external
 * ones. "Senior" is the only extra permission (reopen a closed record). Members are deactivated, never deleted.
 * Below: the merchant → KAM mapping that pre-selects the KAM on a client attributable delay.
 * Access: PTL_EDD_ADMIN (route + menu + every write endpoint).
 */
export default function TeamMaster() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: masters, isLoading, error } = useNabdMastersQuery()
  const [setActive, { isLoading: toggling }] = useNabdSetMemberActiveMutation()
  const [editing, setEditing] = useState(null) // null = closed, {} = add, member = edit
  const [confirm, setConfirm] = useState(null)

  const team = useMemo(() => masters?.team || [], [masters])
  const open = masters?.openCounts || {}
  const kams = team.filter((m) => canAct(m, 'KAM')).sort((a, b) => a.name.localeCompare(b.name))
  const ops = team.filter((m) => canAct(m, 'PTL_OPS')).sort((a, b) => (a.subTeam || '').localeCompare(b.subTeam || '') || a.name.localeCompare(b.name))
  const active = (list) => list.filter((m) => m.active)
  const seniors = active(team).filter((m) => m.senior).length
  const canEdit = !!masters?.canEditMasters

  const toggle = async (m) => {
    try {
      await setActive({ id: m.id, active: !m.active }).unwrap()
      enqueueSnackbar(`${m.name} ${m.active ? 'deactivated' : 'activated'}`, { variant: 'success' })
      setConfirm(null)
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Something went wrong', { variant: 'error' })
    }
  }
  const askToggle = (m) => (m.active && (open[m.id] || 0) > 0 ? setConfirm(m) : toggle(m))

  const memberTable = (title, chipOwner, rows, note, showSub) => (
    <Card sx={{ boxShadow: 'none', border: `1px solid ${palette.grey[300]}`, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${palette.grey[300]}` }}>
        <Box component='span' sx={{ px: 1, py: 0.25, borderRadius: '4px', fontSize: 11, fontWeight: 700, color: OWNERS[chipOwner].color, bgcolor: OWNERS[chipOwner].bg }}>{title}</Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{note}</Typography>
        <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.secondary' }}>{active(rows).length} active</Typography>
      </Box>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={tableHeadSx}>Member</TableCell>
            {showSub && <TableCell sx={{ ...tableHeadSx, width: 170 }}>Sub-team</TableCell>}
            <TableCell sx={{ ...tableHeadSx, width: 230 }}>Login</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: 110 }}>Can reopen</TableCell>
            <TableCell sx={{ ...tableHeadSx, width: 80 }} align='right'>Open</TableCell>
            {canEdit && <TableCell sx={{ ...tableHeadSx, width: 190 }}>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary', fontSize: 12 }}>Nobody on this team yet.</TableCell></TableRow>
          )}
          {rows.map((m) => (
            <TableRow key={m.id} sx={{ opacity: m.active ? 1 : 0.55 }}>
              <TableCell>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {m.name}{(m.roles || []).length > 1 && <Chip size='small' label={rolesLabel(m, TEAM_SHORT_LABEL).toUpperCase()} sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                  {!m.active && <Chip size='small' label='INACTIVE' sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{m.designation}</Typography>
              </TableCell>
              {showSub && (
                <TableCell>
                  <Box component='span' sx={{ px: 1, py: 0.25, borderRadius: '4px', fontSize: 11, fontWeight: 700,
                    bgcolor: m.subTeam === 'FTR' ? palette.success.lighter : palette.primary.lighter,
                    color: m.subTeam === 'FTR' ? palette.success.darker : palette.primary.dark }}>{SUB_TEAM_LABEL[m.subTeam] || '—'}</Box>
                </TableCell>
              )}
              <TableCell sx={{ fontSize: 12, color: m.email ? 'text.primary' : palette.error.dark }}>{m.email || 'Not linked, cannot act'}</TableCell>
              <TableCell>{m.senior
                ? <Box component='span' sx={{ px: 1, py: 0.25, borderRadius: '4px', fontSize: 11, fontWeight: 700, color: OWNERS.CLIENT.color, bgcolor: OWNERS.CLIENT.bg }}>SENIOR</Box>
                : <Typography component='span' sx={{ color: 'text.disabled' }}>—</Typography>}</TableCell>
              <TableCell align='right' sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{open[m.id] || 0}</TableCell>
              {canEdit && (
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size='small' variant='outlined' onClick={() => setEditing(m)} sx={{ textTransform: 'none' }}>Edit</Button>
                    <Button size='small' variant='outlined' color={m.active ? 'error' : 'primary'} disabled={toggling}
                      onClick={() => askToggle(m)} sx={{ textTransform: 'none' }}>{m.active ? 'Deactivate' : 'Activate'}</Button>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  )

  return (
    <Box>
      <PageTitle>PTL Team Master</PageTitle>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Who sits in the PTL team, and what each person can be assigned. A client attributable delay can only be assigned to a
        Key Account Manager. A Prozo attributable or external delay can only be assigned to a PTL Operations member, core operations or first time right.
      </Typography>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error?.data?.message || error?.message || 'Could not load the team.'}</Alert>}

      {isLoading ? <Skeleton variant='rectangular' height={360} /> : !error && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {[
              { label: `${active(team).length} active members`, color: palette.info.darker, bg: palette.info.lighter },
              { label: `${active(kams).length} Key Account Managers`, color: OWNERS.CLIENT.color, bg: OWNERS.CLIENT.bg },
              { label: `${active(ops).filter((m) => m.subTeam === 'CORE_OPS').length} Core Operations`, color: palette.primary.dark, bg: palette.primary.lighter },
              { label: `${active(ops).filter((m) => m.subTeam === 'FTR').length} First Time Right`, color: palette.success.darker, bg: palette.success.lighter },
              { label: `${seniors} senior, can reopen a closed record`, color: palette.warning.darker, bg: palette.warning.lighter },
              ...(team.some((m) => m.active && !m.email) ? [{ label: `${team.filter((m) => m.active && !m.email).length} not linked to a login`, color: palette.error.dark, bg: palette.error.lighter }] : []),
            ].map((p) => (
              <Box key={p.label} component='span' sx={{ px: 1.25, py: 0.5, borderRadius: '6px', fontSize: 12, fontWeight: 600, color: p.color, bgcolor: p.bg }}>{p.label}</Box>
            ))}
            {canEdit && <Button variant='contained' onClick={() => setEditing({})} sx={{ ml: 'auto' }}>+ Add member</Button>}
          </Box>

          {memberTable('KAM', 'CLIENT', kams, 'Key Account Managers, take client attributable delays', false)}
          {memberTable('PTL OPS', 'PROZO', ops, 'PTL Operations, take Prozo attributable and external delays', true)}

          <Card sx={{ boxShadow: 'none', border: `1px solid ${palette.grey[300]}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${palette.grey[300]}` }}>
              <StorefrontOutlinedIcon fontSize='small' sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Merchant to KAM mapping</Typography>
              <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.secondary' }}>{(masters?.kamMapping || []).length} merchants mapped</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>A client attributable delay pre-selects the KAM mapped here.</Typography>
              <KamMappingEditor masters={masters} canEdit={canEdit} />
            </Box>
          </Card>
        </>
      )}

      <MemberDialog open={!!editing} member={editing && editing.id ? editing : null} team={team}
        openCount={editing?.id ? (open[editing.id] || 0) : 0} onClose={() => setEditing(null)} />
      <ConfirmDialog open={!!confirm} title={`Deactivate ${confirm?.name}?`} danger confirmLabel='Deactivate' loading={toggling}
        message={`${confirm?.name} holds ${open[confirm?.id] || 0} open shipment(s). Deactivating hides them from new assignment and removes their merchant mappings; the open shipments stay with them.`}
        onClose={() => setConfirm(null)} onConfirm={() => toggle(confirm)} />
    </Box>
  )
}
